import { useState, useEffect, useCallback, useRef } from 'react';
import { ApolloError } from 'apollo-client';
import { getLogger } from 'src/utils/datadog-logger';
import { useObserver } from 'mobx-react-lite';

import PublicEnv from 'shared/utils/public-env';
import useStores from 'shared/hooks/use-stores';
import { PusherConfig } from 'shared/constants';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { useCreateInstoreCartUrlMutation, useUpdateKioskOrderPaymentMutation } from 'types/graphql';
import { useCountdownTimer } from 'src/hooks/use-countdown-timer';
import { DUTCHIE_PAY, LOG_NAMESPACES } from 'src/payments/constants';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { AnalyticsOrder } from 'src/utils/analytics/events';
import { tracker } from 'src/utils/analytics/trackers';

const logger = getLogger(LOG_NAMESPACES.PBB_KIOSK_CHECKOUT);

function buildInstoreCartUrl(urlString: string): string {
  const consumerUrlString = PublicEnv.consumerUrl;
  const url = new URL(urlString);
  const newUrl = new URL(url.pathname + url.search + url.hash, consumerUrlString);

  return newUrl.href;
}

type UseKioskSelectPaymentParams = {
  onClose: () => void;
};

type UseKioskSelectPaymentReturn = {
  counterPaymentOptions: DispensaryPaymentOptions;
  dispensary: ReturnType<typeof useDispensary>['dispensary'];
  error?: ApolloError;
  handleOnClose: () => void;
  handleOnErrorClose: () => void;
  handleUpdateOrderPayment: () => Promise<void>;
  instoreCartUrl: string | null;
  isAnonymousKioskCheckout: boolean;
  loading: boolean;
  onPaymentSelect: (value: string) => void;
  orderNumber: string | null;
  pbbEnabled: boolean;
  pbbOption: DispensaryPaymentOptions[number] | undefined;
  secondsLeft: number;
};

type DispensaryPaymentOptions = NonNullable<ReturnType<typeof useDispensaryPaymentMethods>>;

export function useKioskSelectPayment({ onClose }: UseKioskSelectPaymentParams): UseKioskSelectPaymentReturn {
  const { dispensary } = useDispensary();
  const [instoreCartUrl, setInstoreCartUrl] = useState<string | null>(null);
  const [cartViewed, setCartViewed] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string>('');
  const [updateOrderError, setUpdateOrderError] = useState<ApolloError | undefined>();
  const isHandlingClose = useRef(false);
  const updatingPaymentMethodRef = useRef<string>('');
  const { Cart, UI, pusher, User } = useStores();
  const { checkoutToken, setPaymentMethod, setPaymentMethodId } = Cart;
  const { isDutchiePayEnabledForDispo } = useDutchiePay();
  const [createInstoreCartUrl, { loading: mutationLoading, error: createUrlError }] = useCreateInstoreCartUrlMutation();
  const isAnonymousKioskCheckout = Cart?.isAnonymousKioskCheckout ?? false;
  const orderNumber = Cart?.anonymousOrderId ?? null;
  const allPaymentOptions = useDispensaryPaymentMethods() ?? [];
  const pbbOption = allPaymentOptions.find((option) => option.value === DUTCHIE_PAY && !option.disabled);
  const pbbEnabled = allPaymentOptions.some((option) => option.value === DUTCHIE_PAY && !option.disabled);
  const counterPaymentOptions = allPaymentOptions.filter((option) => option.value !== DUTCHIE_PAY && !option.disabled);
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);
  const dispoCname = dispensary?.cName;
  const loading =
    (!createUrlError && !updateOrderError && mutationLoading) || (pbbEnabled && !instoreCartUrl) || !Cart.serverOrderId;

  const getCommonLogContext = useCallback(
    (): Record<string, boolean | string> => ({
      dispensaryId: Cart.dispensary?.id ?? 'unknown',
      checkoutToken: Cart.checkoutToken ?? 'unknown',
      updateOrderToken: Cart.updateOrderToken ?? 'unknown',
      orderId: Cart.serverOrderId ?? 'unknown',
      pbbEnabled,
      paymentMethod: Cart.paymentMethod ?? 'unknown',
      paymentMethodId: Cart.paymentMethodId ?? 'unknown',
      isAnonymousKioskCheckout,
    }),
    [
      Cart.dispensary?.id,
      Cart.checkoutToken,
      Cart.updateOrderToken,
      Cart.serverOrderId,
      pbbEnabled,
      Cart.paymentMethod,
      Cart.paymentMethodId,
      isAnonymousKioskCheckout,
    ]
  );

  const resetCart = useCallback((): void => {
    Cart.clearOrder();
    Cart.setAnonymousOrderId('');
    Cart.setIsAnonymousKioskCheckout(false);
    UI.showMenu(UI.embeddedCName);
  }, [Cart, UI]);

  function afterUpdateKioskOrderPayment(data: { updateKioskOrderPayment: { success: boolean } }): void {
    if (data.updateKioskOrderPayment.success) {
      UI.showKioskOrderSuccess = true;

      const trackedOrder = {
        ...Cart.order,
        paymentMethod: updatingPaymentMethodRef.current,
      };

      if (isDutchiePayEnabledForDispo && pbbEnabled) {
        tracker.placedOrder({
          checkoutToken,
          order: trackedOrder as AnalyticsOrder,
          isDutchiePayEnabledForDispo,
          isEnrolledDutchiePay: false,
        });
      }
    }
    UI.updateShowKioskSelectPaymentModal(false);
    resetCart();
  }

  const [updateOrderPayment] = useUpdateKioskOrderPaymentMutation({
    onCompleted: afterUpdateKioskOrderPayment,
  });

  const handleUpdateOrderPayment = useCallback(
    async (paymentMethod = Cart.paymentMethod): Promise<void> => {
      try {
        setUpdateOrderError(undefined);
        // Store the payment method being updated for accurate tracking
        updatingPaymentMethodRef.current = paymentMethod;
        await updateOrderPayment({
          variables: {
            input: {
              dispensaryId: Cart.dispensary?.id,
              id: Cart.serverOrderId,
              updateOrderToken: Cart.updateOrderToken,
              paymentMethod,
              paymentMethodId: Cart.paymentMethodId,
            },
          },
        });
      } catch (e) {
        setUpdateOrderError(e as ApolloError);
        logger.error('Kiosk Update Order Payment Error', {
          ...getCommonLogContext(),
          e,
        });
        throw e;
      }
    },
    [
      Cart.dispensary?.id,
      Cart.paymentMethodId,
      Cart.paymentMethod,
      Cart.serverOrderId,
      Cart.updateOrderToken,
      getCommonLogContext,
      updateOrderPayment,
    ]
  );

  const handleOnClose = useCallback((): void => {
    // Make sure we don't call onClose multiple times
    if (isHandlingClose.current) {
      return;
    }

    isHandlingClose.current = true;
    const closeAndReset = (): void => {
      onClose();
      resetCart();
      isHandlingClose.current = false;
    };

    if (!cartViewed) {
      handleUpdateOrderPayment(defaultPaymentMethod)
        .then(closeAndReset)
        .catch((e) => {
          console.error(e);
          closeAndReset();
        });
    } else {
      closeAndReset();
    }
  }, [cartViewed, onClose, resetCart, handleUpdateOrderPayment, defaultPaymentMethod]);

  const handleOnErrorClose = useCallback(() => {
    resetCart();
    if (isUserLoggedIn) {
      User?.logout();
    }

    UI.redirectToKioskLockScreen(dispoCname);
    onClose();
  }, [onClose, resetCart, isUserLoggedIn, User, UI, dispoCname]);

  const handleDidReceiveCartLoaded = useCallback(() => {
    setCartViewed(true);
    onClose();
    resetCart();
    UI.updateShowDutchiePayKioskOrderSuccess(true);
  }, [resetCart, onClose, UI]);

  const { secondsLeft, startTimer } = useCountdownTimer({
    initialSeconds: 60,
    onTimerStop: handleOnClose,
  });

  const onPaymentSelect = useCallback(
    (value: string): void => {
      setPaymentMethod(value);
      setPaymentMethodId(null);
    },
    [setPaymentMethod, setPaymentMethodId]
  );

  useEffect(() => {
    if (loading || createUrlError || updateOrderError) {
      return;
    }

    startTimer();
  }, [loading, createUrlError, updateOrderError, startTimer]);

  // Set default payment method when counterPaymentOptions is first available
  useEffect(() => {
    if (counterPaymentOptions.length > 0 && !defaultPaymentMethod) {
      setDefaultPaymentMethod(counterPaymentOptions[0].value);
    }
  }, [counterPaymentOptions, defaultPaymentMethod]);

  // Handle the Pusher event when a user views the cart on their device
  useEffect(() => {
    if (Cart.serverOrderId) {
      const instoreCartChannel = pusher.subscribe(PusherConfig.InstoreCart.CHANNEL(Cart.serverOrderId), 'cart_loaded');
      instoreCartChannel.bind('cart_loaded', handleDidReceiveCartLoaded);

      return () => {
        instoreCartChannel.unbind('cart_loaded', handleDidReceiveCartLoaded);
        pusher.unsubscribe(PusherConfig.InstoreCart.CHANNEL(Cart.serverOrderId));
      };
    }

    return undefined;
  }, [Cart.serverOrderId, handleDidReceiveCartLoaded, pusher]);

  // Create the instore cart url
  useEffect(() => {
    const createCartUrl = async (): Promise<void> => {
      if (createUrlError) {
        logger.error('Kiosk Create Instore Cart Cannot Continue', {
          ...getCommonLogContext(),
          createUrlError: createUrlError.message,
          instoreCartUrl: instoreCartUrl ?? 'unknown',
        });
        return;
      }

      if (!pbbEnabled || !Cart.serverOrderId || instoreCartUrl) {
        return;
      }

      try {
        const result = await createInstoreCartUrl({
          variables: {
            orderId: Cart.serverOrderId,
          },
        });

        const instoreCartUrlResult = result.data?.createInstoreCartUrl?.instoreCartUrl ?? null;

        if (!instoreCartUrlResult) {
          throw new Error('No instore cart url returned');
        }

        const additionalParams = new URLSearchParams({
          isKiosk: 'true',
          checkoutToken: Cart.checkoutToken,
          updateOrderToken: Cart.updateOrderToken,
          orderId: Cart.serverOrderId,
        });
        const completeUrl = `${instoreCartUrlResult}&${additionalParams.toString()}`;
        setInstoreCartUrl(buildInstoreCartUrl(completeUrl));
      } catch (e) {
        console.error(e);
        logger.error('Kiosk Create Instore Cart Url Error', {
          ...getCommonLogContext(),
          e,
        });
      }
    };

    void createCartUrl();
  }, [
    Cart.checkoutToken,
    Cart.updateOrderToken,
    createInstoreCartUrl,
    createUrlError,
    getCommonLogContext,
    instoreCartUrl,
    pbbEnabled,
    Cart.serverOrderId,
  ]);

  return {
    counterPaymentOptions,
    dispensary,
    error: createUrlError ?? updateOrderError,
    handleOnClose,
    handleOnErrorClose,
    handleUpdateOrderPayment,
    instoreCartUrl,
    isAnonymousKioskCheckout,
    loading,
    onPaymentSelect,
    orderNumber,
    pbbEnabled,
    pbbOption,
    secondsLeft,
  };
}
